import DebounceManager from '../debounce/DebounceManager.js';
import IframeUtility from './IframeUtility.js';
import IframeVisibleArea from './IframeVisibleArea.js';

/**
 * IframeVisibleAreaObserver.
 */
export default class IframeVisibleAreaObserver {
	private callback: (() => void) | null = null;
	private debounce = new DebounceManager();
	private debounceTime: number | null = null;
	private overflow = false;
	private isInsideIframe = IframeUtility.isInsideIframe();
	private isParentWindowAccessible = IframeUtility.isParentWindowAccessible();

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.callback ...
	 * @param options.overflow ...
	 * @param options.debounceTime ...
	 */
	constructor(options: {
		callback: (area: { top: number; bottom: number }) => void;
		overflow: boolean;
		debounceTime: number;
	}) {
		this.debounceTime = options ? options.debounceTime : null;
		this.overflow = options ? options.overflow : false;
		this.callback = options
			? (): void => options.callback(IframeVisibleArea.getArea(this.overflow))
			: null;
	}

	/**
	 * Start dispatching update events.
	 */
	public observe(): void {
		if (this.isInsideIframe && this.isParentWindowAccessible) {
			window.parent.addEventListener('scroll', this.updateListener);
			window.parent.addEventListener('resize', this.updateListener);
			window.addEventListener('resize', this.updateListener);
		} else {
			throw 'Parent window is not accessible or IframeVisibleAreaObserver will only work from within an iframe. Use position: sticky; instead';
		}
	}

	/**
	 * Removes listeners.
	 */
	public disconnect(): void {
		if (this.isInsideIframe && this.isParentWindowAccessible) {
			window.parent.removeEventListener('scroll', this.updateListener);
			window.parent.removeEventListener('resize', this.updateListener);
			window.removeEventListener('resize', this.updateListener);
			this.debounce.clearAll();
		}
	}

	/**
	 * Returns bottom, top, left and right.
	 *
	 * @returns Position.
	 */
	public getArea(): { top: number; bottom: number } {
		return IframeVisibleArea.getArea(false);
	}

	private updateListener = (): void => {
		if (!this.callback) {
			return;
		}

		if (this.debounceTime) {
			this.debounce.set('update', this.callback, this.debounceTime);
		} else {
			this.callback();
		}
	};
}
